import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import TableViewIcon from "@mui/icons-material/TableView";
import { endOfDay, format, isSameDay, startOfDay } from "date-fns";
import StandardTable from "../../../tables/StandardTable";
import { downloadExcel } from "../../../../utils/function";
import { GETPROJECTACTIVITYDETAILS, IActivityDetail, IMetric } from "./types";

const APPOINTMENT_FLAGS: { key: "confirmed" | "cameIn" | "cancelled" | "noResponse"; label: string; color: string }[] = [
  { key: "confirmed", label: "Confirmed", color: "#2e7d32" },
  { key: "cameIn", label: "Showed Up", color: "#2e7d32" },
  { key: "cancelled", label: "Cancelled", color: "#d32f2f" },
  { key: "noResponse", label: "No Response", color: "#ed6c02" },
];

export type DetailsRequest = { type: "metric"; metric: IMetric; dateStart: Date; dateEnd: Date };

// +14374769688 / 4374769688 -> (437) 476-9688
export const formatPhone = (phone: string) => {
  const digits = (phone || "").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
  return digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : phone;
};

const formatDuration = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const rangeLabel = (dateStart: Date, dateEnd: Date) =>
  isSameDay(dateStart, dateEnd) ? format(dateStart, "MMM d, yyyy") : `${format(dateStart, "MMM d, yyyy")} - ${format(dateEnd, "MMM d, yyyy")}`;

// Sort on the timestamp, display formatted
const dateColumn = (Header: string, getDate: (row: IActivityDetail) => Date | undefined) => ({
  Header,
  id: Header,
  accessor: (rowData: IActivityDetail) => {
    const date = getDate(rowData);
    return date ? new Date(date).valueOf() : 0;
  },
  Cell: ({ value }: { value: number }) => <span style={{ whiteSpace: "nowrap" }}>{value ? format(new Date(value), "MMM d, yyyy h:mm a") : "-"}</span>,
  exportValue: (value: number) => (value ? format(new Date(value), "MMM d, yyyy h:mm a") : ""),
});

// Exports the rows using the table's own columns. exportValue formats values whose Cell renders something else
const exportToExcel = (columns: any[], rows: any[], title: string) => {
  const headers = columns.map((column, index) => ({ id: `column${index}`, label: column.Header }));
  const data = rows.map((row, rowIndex) =>
    Object.fromEntries(
      columns.map((column, index) => {
        const value = column.accessor(row, rowIndex);
        return [`column${index}`, column.exportValue ? column.exportValue(value) : value];
      })
    )
  );
  const widths = [[Object.fromEntries(headers.map((header) => [header.id, 25]))]];
  // Excel sheet names are limited to 31 characters and cannot contain [ ] : * ? / \
  const sheetName = title.replace(/[[\]:*?/\\]/g, "").slice(0, 31);
  downloadExcel([data], [headers], [], widths, [sheetName], `${title}.xlsx`);
};

const ExcelButton = (props: { onClick: () => void; disabled: boolean }) => (
  <IconButton onClick={props.onClick} disabled={props.disabled} title="Download Excel">
    <TableViewIcon sx={{ color: props.disabled ? undefined : "success.main" }} />
  </IconButton>
);

const DetailsDialog = (props: ChildProps) => {
  const { project, request, onClose } = props;

  return (
    <Dialog open={!!request} onClose={onClose} maxWidth="lg" fullWidth>
      {request ? (
        <>
          <DialogTitle sx={{ pr: 6 }}>
            <strong>{request.metric.label}</strong>
            <Typography variant="body2" color="text.secondary">
              {rangeLabel(request.dateStart, request.dateEnd)}
            </Typography>
            <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <MetricDetails project={project} {...request} />
          </DialogContent>
        </>
      ) : null}
    </Dialog>
  );
};

const MetricDetails = (props: { project: string; metric: IMetric; dateStart: Date; dateEnd: Date }) => {
  const { project, metric, dateStart, dateEnd } = props;
  const { data, loading, error } = useQuery(GETPROJECTACTIVITYDETAILS, {
    fetchPolicy: "network-only",
    variables: { project, metric: metric.key, dateStart: startOfDay(dateStart), dateEnd: endOfDay(dateEnd) },
  });
  const rows: IActivityDetail[] = useMemo(() => data?.projectActivityDetails || [], [data]);

  const columns = useMemo(() => {
    const index = {
      Header: "#",
      id: "index",
      accessor: (_: IActivityDetail, index: number) => index + 1,
    };
    const user = metric.userLabel
      ? [
          {
            Header: metric.userLabel,
            accessor: (rowData: IActivityDetail) => rowData.user || "-",
          },
        ]
      : [];

    if (metric.call) {
      return [
        index,
        dateColumn(metric.dateLabel, (rowData) => rowData.date),
        {
          Header: "From",
          accessor: (rowData: IActivityDetail) => formatPhone(rowData.call?.from || "") || "-",
        },
        {
          Header: "To",
          accessor: (rowData: IActivityDetail) => formatPhone(rowData.call?.to || "") || "-",
        },
        ...user,
        {
          Header: "Duration",
          id: "duration",
          accessor: (rowData: IActivityDetail) => rowData.call?.duration || 0,
          Cell: ({ value }: { value: number }) => formatDuration(value),
          exportValue: (value: number) => formatDuration(value),
        },
        {
          Header: "Status",
          accessor: (rowData: IActivityDetail) => (rowData.call?.status || "-").replace(/-/g, " "),
        },
      ];
    }

    return [
      index,
      dateColumn(metric.dateLabel, (rowData) => rowData.date),
      {
        Header: "Name",
        accessor: (rowData: IActivityDetail) => rowData.name || "-",
      },
      {
        Header: "Email",
        accessor: (rowData: IActivityDetail) => rowData.email || "-",
      },
      ...user,
      ...(metric.appointment === "created" ? [dateColumn("Appointment Date", (rowData) => rowData.appointment?.date)] : []),
      ...(metric.appointment
        ? [
            {
              Header: "Location",
              accessor: (rowData: IActivityDetail) => rowData.appointment?.location || "-",
            },
            {
              Header: "Status",
              accessor: (rowData: IActivityDetail) => (rowData.appointment?.status ? rowData.appointment.status.replace(/_/g, " ") : "-"),
            },
            ...APPOINTMENT_FLAGS.map(({ key, label, color }) => ({
              Header: label,
              id: key,
              accessor: (rowData: IActivityDetail) => (rowData.appointment?.[key] ? 1 : 0),
              Cell: ({ value }: { value: number }) => (value ? <CheckIcon fontSize="small" sx={{ color }} /> : null),
              exportValue: (value: number) => (value ? "Yes" : ""),
            })),
          ]
        : [
            {
              Header: metric.detailLabel || "Details",
              accessor: (rowData: IActivityDetail) => rowData.detail || "-",
              Cell: ({ value }: { value: string }) => <span style={{ whiteSpace: "pre-wrap" }}>{value}</span>,
            },
          ]),
    ];
  }, [metric]);

  if (error) return <Typography color="error">Error getting details</Typography>;

  const title = `${metric.label} ${rangeLabel(dateStart, dateEnd)}`;

  return (
    <StandardTable
      columns={columns}
      data={rows}
      loading={loading}
      buttons={<ExcelButton disabled={loading || !rows.length} onClick={() => exportToExcel(columns, rows, title)} />}
    />
  );
};

interface ChildProps {
  project: string;
  request: DetailsRequest | null;
  onClose: () => void;
}

export default DetailsDialog;
